import type { GroupSelection } from './groupFilter';
import type { MakerFlag } from '../../types/entry.types';
import type { HedgeMode } from '../shared/counterHedgeMode';

/**
 * Group-aware payload builders for the two "apply to all" bulk actions on
 * the opener tab. Both keep the no-filter ("All groups") behavior byte-
 * identical to the pre-group payloads — that path is regression-critical.
 */

/** Backend caps `pairIds` at 100 per /pairs/bulk-opener-enabled request. */
export const BULK_ENABLE_PAIR_IDS_CHUNK = 100;
/** Backend caps `ruleIds` at 500 per /rules/bulk-params request. */
export const BULK_PARAMS_RULE_IDS_CHUNK = 500;

export function chunkIds(ids: string[], size: number): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

export interface BulkEnablePayload {
  scope: 'all' | 'ids';
  enabled: boolean;
  pairIds?: string[];
  groupId?: string;
}

/**
 * "All ON / All OFF" payload(s). A selected group narrows the server-side
 * scope:'all' via `groupId`; "Ungrouped" has NO server-side narrowing, so
 * it reuses the scope:'ids' path over the currently-visible (ungrouped)
 * pair ids, chunked at the backend cap.
 */
export function buildBulkEnableAllPayloads(
  selection: GroupSelection,
  enabled: boolean,
  visiblePairIds: string[]
): BulkEnablePayload[] {
  if (selection.kind === 'all') return [{ scope: 'all', enabled }];
  if (selection.kind === 'group') {
    return [{ scope: 'all', enabled, groupId: selection.groupId }];
  }
  return chunkIds(visiblePairIds, BULK_ENABLE_PAIR_IDS_CHUNK).map(pairIds => ({
    scope: 'ids',
    pairIds,
    enabled
  }));
}

/**
 * Honest pair total for the "All ON / All OFF" confirm dialog. "All groups"
 * and a real group flip server-side (`scope:'all'` [+ `groupId`]) — the
 * WHOLE group-scoped universe, not just the search-narrowed visible rows —
 * so the dialog must quote the UNSEARCHED group-scoped pair count.
 * "Ungrouped" rides the scope:'ids' path over the visible rows, so there
 * the visible selectable count is the accurate one.
 */
export function bulkAllConfirmCount(
  selection: GroupSelection,
  groupPairCount: number,
  visibleSelectableCount: number
): number {
  return selection.kind === 'ungrouped'
    ? visibleSelectableCount
    : groupPairCount;
}

/**
 * Pair ids the "All ON / All OFF" write actually flips — the id-set twin of
 * `bulkAllConfirmCount`. "All groups" and a real group flip server-side
 * (`scope:'all'` [+ `groupId`]), so the post-write settle + unconfirmed
 * re-check must cover the UNSEARCHED group-scoped pair ids, not just the
 * search-narrowed visible rows. "Ungrouped" rides the scope:'ids' path over
 * the visible rows, so there the visible selectable ids are the target.
 */
export function bulkAllTargetIds(
  selection: GroupSelection,
  groupPairIds: string[],
  visibleSelectableIds: string[]
): string[] {
  return selection.kind === 'ungrouped' ? visibleSelectableIds : groupPairIds;
}

export interface BulkParamsValues {
  thresholdSum?: number;
  size?: number;
  maxVolumeContracts?: number;
  /**
   * Maker size mode — dynamic sizes the maker to the counter top-of-book,
   * fixed rests the rule's full Size. Callers set the key ONLY when the
   * operator picked a mode (the bar's "—" leaves it out entirely), so the
   * no-mode payload stays byte-identical to the pre-mode one.
   */
  makerSizeMode?: 'dynamic' | 'fixed';
  /**
   * Counter-stuck market-hedge timeout (seconds). Callers set the key ONLY
   * when the operator filled the field (blank leaves it out entirely), same
   * contract as `makerSizeMode` — the no-value payload stays byte-identical.
   */
  counterStuckMarketHedgeSeconds?: number;
  /**
   * Keep posting the Polymarket maker even when the book sits far from the
   * threshold (illiquid markets). Callers set the key ONLY when the operator
   * toggled it (the bar's indeterminate state leaves it out entirely), same
   * contract as `makerSizeMode` — the no-value payload stays byte-identical.
   */
  disablePlacementGatePolymarket?: boolean;
  /** Same as `disablePlacementGatePolymarket` but for the Kalshi maker leg. */
  disablePlacementGateKalshi?: boolean;
  /**
   * Send the Polymarket maker with `postOnly` (the venue rejects it instead of
   * executing when it would be marketable, and the engine reprices). Callers
   * set the key ONLY when the operator toggled it (the bar's indeterminate
   * state leaves it out entirely), same contract as `disablePlacementGate*` —
   * the no-value payload stays byte-identical.
   */
  polyMakerPostOnly?: boolean;
  counterMakerWalkStepSeconds?: number | null;
  counterMakerWalkStepCents?: number | null;
  lockstepMaxAheadBatches?: number | null;
  /**
   * Counter-hedge pricing mode override. `null` clears the per-rule override
   * back to the global default — so this key, when the caller sets it, is
   * ALWAYS emitted (an explicit `null` for inherit), never omitted. The
   * dedicated hedge-mode bulk apply (`buildBulkHedgeModeDtos`) always sets it.
   */
  counterHedgeMode?: HedgeMode | null;
  /**
   * Group-wide forfait-protection arm/disarm — the bulk twin of the per-row
   * Forf checkbox. Callers set the key ONLY when the operator moved the
   * tristate off "no change", same contract as `disablePlacementGate*`.
   */
  forfaitProtectionEnabled?: boolean;
  /**
   * Group-wide Polymarket buy ceiling while the forfait regime is armed. Set
   * ONLY when the operator filled the field; a blank leaves it out entirely
   * (there is no "clear to inherit" here — the engine's own default rules).
   */
  forfaitPolyMaxBuyPrice?: number;
}

export interface BulkParamsDto extends BulkParamsValues {
  scope: 'all' | 'ids';
  ruleIds?: string[];
  /**
   * Server-side group narrowing for `scope:'all'` — stamps only that opener
   * group's rules (paused included), groupId XOR subgroupId, never both.
   */
  groupId?: string;
  subgroupId?: string;
  path: 'a' | 'b';
}

/**
 * Scope a bulk-params apply can target: the page's group filter (all / a real
 * group / ungrouped) OR a concrete subgroup nested inside the selected group.
 * Mirrors `BulkMakerSelection` — the subgroup branch is server-narrowed via
 * `subgroupId` instead of shipping the group-scoped rule ids (groupId XOR
 * subgroupId, never both).
 */
export type BulkParamSelection =
  | GroupSelection
  | { kind: 'subgroup'; subgroupId: string };

/**
 * Set-all threshold/size/maxV DTO(s) — a RULES op. "All groups" keeps the
 * single scope:'all' request; a group / Ungrouped filter resolves to the
 * PRE-SEARCH group-scoped pairs' rule ids (the whole group, not just the
 * search-narrowed visible rows — consistent with the group "All ON/OFF")
 * and sends scope:'ids', chunked defensively at the backend's 500-id cap;
 * a concrete subgroup narrows scope:'all' server-side via `subgroupId` (so
 * the backend stamps the whole subgroup without the UI shipping ids), exactly
 * like the subgroup maker-flag flip. An empty id list yields NO requests
 * (nothing to update — the caller still stages the pre-rule seed).
 */
export function buildBulkParamsDtos(
  selection: BulkParamSelection,
  path: 'a' | 'b',
  values: BulkParamsValues,
  scopedRuleIds: string[]
): BulkParamsDto[] {
  if (selection.kind === 'all') return [{ scope: 'all', path, ...values }];
  if (selection.kind === 'subgroup') {
    return [{ scope: 'all', subgroupId: selection.subgroupId, path, ...values }];
  }
  return chunkIds(scopedRuleIds, BULK_PARAMS_RULE_IDS_CHUNK).map(ruleIds => ({
    scope: 'ids',
    ruleIds,
    path,
    ...values
  }));
}

/**
 * Applies each bulk-params DTO sequentially and sums the per-chunk
 * `modified` counters into the single number the bar reports ("Updated N
 * rules"). Sequential on purpose — mirrors the other chunked bulk loops so
 * a mid-loop failure leaves a clean prefix applied.
 */
export async function applyBulkParamsDtos(
  dtos: BulkParamsDto[],
  apply: (dto: BulkParamsDto) => Promise<{ modified: number }>
): Promise<number> {
  let totalModified = 0;
  for (const dto of dtos) {
    totalModified += (await apply(dto)).modified;
  }
  return totalModified;
}

/**
 * Bulk counter-hedge-mode DTO(s) — stamps `counterHedgeMode` across the
 * selected scope via the same `/rules/bulk-params` endpoint. Distinct from
 * `buildBulkParamsDtos` (the Thr/Size/MaxV apply) in HOW it narrows a group:
 * it uses the server-side `groupId` scope (like the maker-flag/armed flips)
 * instead of resolving the group to `scope:'ids'` rule ids. "All groups" keeps
 * the single `scope:'all'` request; a real group narrows `scope:'all'`
 * server-side via `groupId`; a concrete subgroup narrows via `subgroupId`
 * (groupId XOR subgroupId, never both); "Ungrouped" rides `scope:'ids'` over
 * the group-scoped (pre-search) rule ids, chunked at the backend's 500-id cap.
 * `counterHedgeMode: null` clears the per-rule override back to the global
 * default and is ALWAYS emitted. An empty id list (ungrouped view with no
 * rules) yields NO requests.
 */
export function buildBulkHedgeModeDtos(
  selection: BulkParamSelection,
  path: 'a' | 'b',
  counterHedgeMode: HedgeMode | null,
  scopedRuleIds: string[]
): BulkParamsDto[] {
  if (selection.kind === 'all')
    return [{ scope: 'all', path, counterHedgeMode }];
  if (selection.kind === 'group')
    return [
      { scope: 'all', groupId: selection.groupId, path, counterHedgeMode }
    ];
  if (selection.kind === 'subgroup')
    return [
      { scope: 'all', subgroupId: selection.subgroupId, path, counterHedgeMode }
    ];
  return chunkIds(scopedRuleIds, BULK_PARAMS_RULE_IDS_CHUNK).map(ruleIds => ({
    scope: 'ids',
    ruleIds,
    path,
    counterHedgeMode
  }));
}

export interface BulkMakerFlagDto {
  scope: 'all' | 'ids';
  ruleIds?: string[];
  groupId?: string;
  subgroupId?: string;
  flag: MakerFlag;
  value: boolean;
}

/**
 * Scope a maker-flag flip can target: the page's group filter (all / a real
 * group / ungrouped), a concrete subgroup nested inside the selected group, OR
 * an explicit set of checked rows. The subgroup branch is server-narrowed
 * exactly like a real group, but via `subgroupId` instead of `groupId` (groupId
 * XOR subgroupId, never both). The `selected` branch carries its own rule ids
 * inline and rides scope:'ids' — it lets the PM/KM ON/OFF controls flip just
 * the checked subset, mirroring the market-data "Enable/Disable selected".
 */
export type BulkMakerSelection =
  | GroupSelection
  | { kind: 'subgroup'; subgroupId: string }
  | { kind: 'selected'; ruleIds: string[] };

/**
 * Bulk maker-flag DTO(s) — flips ONE of the four maker flags across the
 * selected scope. Mirrors `buildBulkParamsDtos`: "All groups" keeps the
 * single scope:'all' request; a real group narrows scope:'all' server-side
 * via `groupId`; a concrete subgroup narrows scope:'all' server-side via
 * `subgroupId` (so the backend flips the whole subgroup without the UI
 * shipping ids); "Ungrouped" rides scope:'ids' over the group-scoped
 * (pre-search) rule ids, chunked at the backend's 500-id cap; "selected"
 * rides scope:'ids' over its own inline checked-row rule ids. An empty id
 * list yields NO requests. `value:true` may ACTIVATE paused rules
 * server-side — the caller's confirm dialog warns about that.
 */
export function buildBulkMakerFlagDtos(
  selection: BulkMakerSelection,
  flag: MakerFlag,
  value: boolean,
  scopedRuleIds: string[]
): BulkMakerFlagDto[] {
  if (selection.kind === 'all') return [{ scope: 'all', flag, value }];
  if (selection.kind === 'group') {
    return [{ scope: 'all', groupId: selection.groupId, flag, value }];
  }
  if (selection.kind === 'subgroup') {
    return [{ scope: 'all', subgroupId: selection.subgroupId, flag, value }];
  }
  // `selected` carries its own rule ids; ungrouped uses the group-scoped ids.
  const ids = selection.kind === 'selected' ? selection.ruleIds : scopedRuleIds;
  return chunkIds(ids, BULK_PARAMS_RULE_IDS_CHUNK).map(ruleIds => ({
    scope: 'ids',
    ruleIds,
    flag,
    value
  }));
}

/**
 * Applies each bulk-maker-flag DTO sequentially and sums the per-chunk
 * `modified` counters. Sequential on purpose — mirrors `applyBulkParamsDtos`
 * so a mid-loop failure leaves a clean prefix applied.
 */
export async function applyBulkMakerFlagDtos(
  dtos: BulkMakerFlagDto[],
  apply: (dto: BulkMakerFlagDto) => Promise<{ modified: number }>
): Promise<number> {
  let totalModified = 0;
  for (const dto of dtos) {
    totalModified += (await apply(dto)).modified;
  }
  return totalModified;
}

export interface BulkArmedDto {
  scope: 'all' | 'ids';
  ruleIds?: string[];
  groupId?: string;
  subgroupId?: string;
  armed: boolean;
}

/**
 * Scope a bulk arm/disarm can target — mirrors `BulkMakerSelection`: the page's
 * group filter (all / a real group / ungrouped), a concrete subgroup nested
 * inside the selected group, OR an explicit set of checked rows. `desiredArmed`
 * is now the ONLY arm/disarm trigger (a maker-flag flip no longer arms), so this
 * is the bulk equivalent of the per-row on/off switch's explicit boolean.
 */
export type BulkArmedSelection =
  | GroupSelection
  | { kind: 'subgroup'; subgroupId: string }
  | { kind: 'selected'; ruleIds: string[] };

/**
 * Bulk arm/disarm DTO(s) — sets `desiredArmed` across the selected scope in one
 * round trip. Byte-identical in structure to `buildBulkMakerFlagDtos` (with
 * `armed` replacing `flag`/`value`): "All groups" keeps the single scope:'all'
 * request; a real group narrows scope:'all' server-side via `groupId`; a
 * concrete subgroup narrows scope:'all' server-side via `subgroupId`;
 * "Ungrouped" rides scope:'ids' over the group-scoped (pre-search) rule ids,
 * chunked at the backend's 500-id cap; "selected" rides scope:'ids' over its own
 * inline checked-row rule ids. An empty id list yields NO requests. The backend
 * itself fans grouped arming to the right feed pods, so the FE always calls the
 * one `/rules/bulk-armed` endpoint on the core base.
 */
export function buildBulkArmedDtos(
  selection: BulkArmedSelection,
  armed: boolean,
  scopedRuleIds: string[]
): BulkArmedDto[] {
  if (selection.kind === 'all') return [{ scope: 'all', armed }];
  if (selection.kind === 'group') {
    return [{ scope: 'all', groupId: selection.groupId, armed }];
  }
  if (selection.kind === 'subgroup') {
    return [{ scope: 'all', subgroupId: selection.subgroupId, armed }];
  }
  // `selected` carries its own rule ids; ungrouped uses the group-scoped ids.
  const ids = selection.kind === 'selected' ? selection.ruleIds : scopedRuleIds;
  return chunkIds(ids, BULK_PARAMS_RULE_IDS_CHUNK).map(ruleIds => ({
    scope: 'ids',
    ruleIds,
    armed
  }));
}

/**
 * Applies each bulk-armed DTO sequentially and sums the per-chunk `modified`
 * counters. Sequential on purpose — mirrors `applyBulkMakerFlagDtos` so a
 * mid-loop failure leaves a clean prefix applied.
 */
export async function applyBulkArmedDtos(
  dtos: BulkArmedDto[],
  apply: (dto: BulkArmedDto) => Promise<{ modified: number }>
): Promise<number> {
  let totalModified = 0;
  for (const dto of dtos) {
    totalModified += (await apply(dto)).modified;
  }
  return totalModified;
}
